import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import Placeholder from '@tiptap/extension-placeholder'
import { Color } from '@tiptap/extension-color'
import { TextStyle } from '@tiptap/extension-text-style'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  normalizePastedRichTextHtml,
  normalizePastedRichTextText,
} from 'src/common/richTextEditor/tiptapPasteUtils'
import { mediaURL } from 'src/consumer/services'

/**
 * TipTap Editor Component for Therapy Report Free Form Editor
 * Replaces ReactQuill with modern, actively maintained TipTap editor
 */
// Predefined color palette for text color
const TEXT_COLOR_PALETTE = [
  { name: 'Default', value: null },
  { name: 'Black', value: '#000000' },
  { name: 'Red', value: '#dc2626' },
  { name: 'Orange', value: '#ea580c' },
  { name: 'Yellow', value: '#ca8a04' },
  { name: 'Green', value: '#16a34a' },
  { name: 'Blue', value: '#2563eb' },
  { name: 'Purple', value: '#9333ea' },
  { name: 'Pink', value: '#db2777' },
  { name: 'Gray', value: '#4b5563' },
]

// Predefined color palette for background color
const BACKGROUND_COLOR_PALETTE = [
  { name: 'None', value: null },
  { name: 'Yellow', value: '#fef08a' },
  { name: 'Green', value: '#bbf7d0' },
  { name: 'Blue', value: '#bfdbfe' },
  { name: 'Pink', value: '#fce7f3' },
  { name: 'Orange', value: '#fed7aa' },
  { name: 'Purple', value: '#e9d5ff' },
  { name: 'Red', value: '#fecaca' },
  { name: 'Gray', value: '#e5e7eb' },
]

const TipTapEditor = ({
  value,
  onChange,
  placeholder = 'Start writing your free form report...',
  onFocus,
  formStartRef,
  onImageInsertReady, // Callback to provide insertImage function to parent
  onImageButtonClick, // Callback when image button is clicked (to trigger upload)
  /** Optional. When set (e.g. 80), editor uses this as min-height (px) so it stays compact and grows with content. Default uses 500/450px. */
  contentMinHeight,
}) => {
  const editorRef = useRef(null)
  const [showTextColorMenu, setShowTextColorMenu] = useState(false)
  const [showBackgroundColorMenu, setShowBackgroundColorMenu] = useState(false)
  // Track if editor is focused to prevent value prop sync during active typing
  const isEditorFocusedRef = useRef(false)
  // Track the last value we set from user input to avoid unnecessary syncs
  const lastUserInputValueRef = useRef(null)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Configure which features from StarterKit to include
        heading: {
          levels: [1, 2, 3], // Only h1, h2, h3 (matching ReactQuill config)
        },
        bulletList: {
          keepMarks: true,
          keepAttributes: false,
        },
        orderedList: {
          keepMarks: true,
          keepAttributes: false,
        },
        blockquote: {
          HTMLAttributes: {
            class: 'tiptap-blockquote',
          },
        },
        // Exclude link and underline from StarterKit since we'll configure them separately
        link: false,
        underline: false,
      }),
      Underline, // Add underline support with custom configuration
      TextStyle, // Required for Color extension
      Color.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            backgroundColor: {
              default: null,
              parseHTML: element => {
                const bgColor = element.style.backgroundColor
                return bgColor || null
              },
              renderHTML: attributes => {
                const styles = []
                if (attributes.color) {
                  styles.push(`color: ${attributes.color}`)
                }
                if (attributes.backgroundColor) {
                  styles.push(`background-color: ${attributes.backgroundColor}`)
                }
                return styles.length > 0 ? { style: styles.join('; ') } : {}
              },
            },
          }
        },
      }), // Text color extension with background color support
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'tiptap-link',
        },
      }),
      Image.configure({
        inline: false,
        allowBase64: false,
        HTMLAttributes: {
          class: 'tiptap-image',
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML()
      // Track this as user input
      lastUserInputValueRef.current = html
      onChange(html)
    },
    onFocus: ({ editor, event }) => {
      isEditorFocusedRef.current = true
      if (onFocus) {
        onFocus(event)
      }
    },
    onBlur: () => {
      isEditorFocusedRef.current = false
    },
    editorProps: {
      // Let ProseMirror own the paste transaction; we only normalize noisy clipboard markup.
      transformPastedHTML: normalizePastedRichTextHtml,
      transformPastedText: normalizePastedRichTextText,
      attributes: {
        class: 'therapy-tiptap-editor',
      },
      // Prevent auto-focus on mount
      autofocus: false,
    },
    // Prevent auto-focus on creation
    autofocus: false,
  })

  // Store editor ref for external access and prevent auto-focus
  useEffect(() => {
    if (editor) {
      editorRef.current = editor
      
      // Prevent auto-focus on mount - blur immediately if focused
      const prosemirror = editor.view.dom
      if (prosemirror && document.activeElement === prosemirror) {
        prosemirror.blur()
      }
      
      // Also prevent focus on next frame in case it happens asynchronously
      requestAnimationFrame(() => {
        if (prosemirror && document.activeElement === prosemirror) {
          prosemirror.blur()
        }
      })
    }
  }, [editor])

  // Update editor content when value prop changes (but not from internal updates)
  // Only sync when:
  // 1. Editor is not focused (external update, not user typing)
  // 2. Value is different from what we last set from user input
  // This prevents interference with fast typing
  useEffect(() => {
    if (editor && value !== undefined) {
      const currentContent = editor.getHTML()
      const isValueFromUserInput = value === lastUserInputValueRef.current
      
      // Don't sync if:
      // - Editor is focused (user is typing)
      // - Value matches what we just set from user input (to avoid sync loop)
      if (isEditorFocusedRef.current || isValueFromUserInput) {
        return
      }
      
      // Only sync if content is actually different
      if (currentContent !== value) {
        editor.commands.setContent(value || '', false) // false = don't trigger onUpdate
        lastUserInputValueRef.current = null // Reset after external update
      }
    }
  }, [editor, value])

  // Insert image into editor (for image upload functionality)
  const insertImage = useCallback(
    (imageId) => {
      if (editor) {
        const imageUrl = `${mediaURL}${imageId}`
        editor.chain().focus().setImage({ src: imageUrl }).run()
      }
    },
    [editor],
  )

  // Expose insertImage method to parent component
  useEffect(() => {
    if (editor && onImageInsertReady) {
      // Pass insertImage function to parent so it can be called after image upload
      onImageInsertReady(insertImage)
    }
  }, [editor, insertImage, onImageInsertReady])

  // Close color menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showTextColorMenu || 
        showBackgroundColorMenu
      ) {
        const target = event.target
        const isColorMenu = target.closest('.tiptap-color-menu')
        const isColorButton = target.closest('[title="Text Color"], [title="Background Color"]')
        
        if (!isColorMenu && !isColorButton) {
          setShowTextColorMenu(false)
          setShowBackgroundColorMenu(false)
        }
      }
    }

    if (showTextColorMenu || showBackgroundColorMenu) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [showTextColorMenu, showBackgroundColorMenu])

  if (!editor) {
    return null
  }

  const wrapperMinHeight = contentMinHeight != null ? `${contentMinHeight}px` : '500px'
  const editorMinHeight = contentMinHeight != null ? `${contentMinHeight}px` : '450px'
  return (
    <div className="therapy-free-form-editor-tiptap" style={{ width: '100%', height: '100%', minHeight: wrapperMinHeight, backgroundColor: '#fff', display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar */}
      <div className="tiptap-toolbar">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          className={editor.isActive('heading', { level: 1 }) ? 'is-active' : ''}
          title="Heading 1"
        >
          H1
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}
          title="Heading 2"
        >
          H2
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          className={editor.isActive('heading', { level: 3 }) ? 'is-active' : ''}
          title="Heading 3"
        >
          H3
        </button>
        <div className="toolbar-divider" />
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={editor.isActive('bold') ? 'is-active' : ''}
          title="Bold"
        >
          <strong>B</strong>
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={editor.isActive('italic') ? 'is-active' : ''}
          title="Italic"
        >
          <em>I</em>
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={editor.isActive('underline') ? 'is-active' : ''}
          title="Underline"
        >
          <u>U</u>
        </button>
        <div className="toolbar-divider" />
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={editor.isActive('bulletList') ? 'is-active' : ''}
          title="Bullet List"
        >
          •
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={editor.isActive('orderedList') ? 'is-active' : ''}
          title="Ordered List"
        >
          1.
        </button>
        <button
          type="button"
          onClick={() => {
            const url = window.prompt('Enter URL:')
            if (url) {
              editor.chain().focus().setLink({ href: url }).run()
            }
          }}
          className={editor.isActive('link') ? 'is-active' : ''}
          title="Link"
        >
          🔗
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          className={editor.isActive('blockquote') ? 'is-active' : ''}
          title="Blockquote"
        >
          "
        </button>
        {onImageButtonClick && (
          <button
            type="button"
            onClick={onImageButtonClick}
            title="Insert Image"
          >
            🖼️
          </button>
        )}
        <div className="toolbar-divider" />
        {/* Text Color Picker */}
        <div style={{ position: 'relative' }}>
          <button
            type="button"
            onClick={() => {
              setShowTextColorMenu(!showTextColorMenu)
              setShowBackgroundColorMenu(false)
            }}
            className={showTextColorMenu ? 'is-active' : ''}
            title="Text Color"
            style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
          >
            <span style={{ 
              width: '16px', 
              height: '16px', 
              borderRadius: '3px',
              background: editor.getAttributes('textStyle').color || '#1f2937',
              border: '1px solid rgba(0,0,0,0.2)',
              display: 'inline-block',
            }} />
            <span style={{ fontSize: '11px' }}>A</span>
          </button>
          {showTextColorMenu && (
            <div className="tiptap-color-menu">
              {TEXT_COLOR_PALETTE.map((color) => (
                <button
                  key={color.name}
                  type="button"
                  onClick={() => {
                    if (color.value) {
                      editor.chain().focus().setColor(color.value).run()
                    } else {
                      editor.chain().focus().unsetColor().run()
                    }
                    setShowTextColorMenu(false)
                  }}
                  className={
                    (color.value && editor.isActive('textStyle', { color: color.value })) ||
                    (!color.value && !editor.getAttributes('textStyle').color)
                      ? 'is-active'
                      : ''
                  }
                  title={color.name}
                >
                  <span
                    style={{
                      width: '18px',
                      height: '18px',
                      borderRadius: '3px',
                      background: color.value || 'linear-gradient(135deg, #fff 45%, #ccc 45%, #ccc 55%, #fff 55%)',
                      border: '1px solid rgba(0,0,0,0.15)',
                      display: 'inline-block',
                    }}
                  />
                  <span style={{ fontSize: '12px', marginLeft: '6px' }}>{color.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {/* Background Color Picker */}
        <div style={{ position: 'relative' }}>
          <button
            type="button"
            onClick={() => {
              setShowBackgroundColorMenu(!showBackgroundColorMenu)
              setShowTextColorMenu(false)
            }}
            className={showBackgroundColorMenu ? 'is-active' : ''}
            title="Background Color"
            style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
          >
            <span style={{ 
              width: '16px', 
              height: '16px', 
              borderRadius: '3px',
              background: editor.getAttributes('textStyle')?.backgroundColor || 'transparent',
              border: '1px solid rgba(0,0,0,0.2)',
              display: 'inline-block',
              backgroundImage: !editor.getAttributes('textStyle')?.backgroundColor 
                ? 'linear-gradient(135deg, #fff 45%, #ccc 45%, #ccc 55%, #fff 55%)' 
                : 'none',
            }} />
            <span style={{ fontSize: '11px' }}>Bg</span>
          </button>
          {showBackgroundColorMenu && (
            <div className="tiptap-color-menu">
              {BACKGROUND_COLOR_PALETTE.map((color) => (
                <button
                  key={color.name}
                  type="button"
                  onClick={() => {
                    if (color.value) {
                      // Set background color - preserve existing color if any
                      const currentAttrs = editor.getAttributes('textStyle')
                      editor.chain().focus().setMark('textStyle', {
                        ...currentAttrs,
                        backgroundColor: color.value,
                      }).run()
                    } else {
                      // Remove background color but preserve text color
                      const currentAttrs = editor.getAttributes('textStyle')
                      const { backgroundColor, ...restAttrs } = currentAttrs
                      if (restAttrs.color) {
                        editor.chain().focus().setMark('textStyle', restAttrs).run()
                      } else {
                        editor.chain().focus().unsetMark('textStyle').run()
                      }
                    }
                    setShowBackgroundColorMenu(false)
                  }}
                  className={
                    (color.value && editor.getAttributes('textStyle')?.backgroundColor === color.value) ||
                    (!color.value && !editor.getAttributes('textStyle')?.backgroundColor)
                      ? 'is-active'
                      : ''
                  }
                  title={color.name}
                >
                  <span
                    style={{
                      width: '18px',
                      height: '18px',
                      borderRadius: '3px',
                      background: color.value || 'linear-gradient(135deg, #fff 45%, #ccc 45%, #ccc 55%, #fff 55%)',
                      border: '1px solid rgba(0,0,0,0.15)',
                      display: 'inline-block',
                    }}
                  />
                  <span style={{ fontSize: '12px', marginLeft: '6px' }}>{color.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="toolbar-divider" />
        <button
          type="button"
          onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
          title="Clear Formatting"
        >
          Clear
        </button>
      </div>

      {/* Editor Content */}
      <EditorContent editor={editor} />

      <style dangerouslySetInnerHTML={{
        __html: [
          '.therapy-free-form-editor-tiptap {',
          '  display: flex;',
          '  flex-direction: column;',
          '  width: 100%;',
          '  height: 100%;',
          `  min-height: ${wrapperMinHeight};`,
          '  background-color: #fff;',
          '  border-radius: 10px;',
          '  border: 1px solid rgba(99, 102, 241, 0.2);',
          '  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);',
          '  overflow: hidden;',
          '  transition: all 0.2s ease;',
          '}',
          '.therapy-free-form-editor-tiptap:focus-within {',
          '  border-color: rgba(99, 102, 241, 0.6);',
          '  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);',
          '}',
          '.tiptap-toolbar {',
          '  display: flex;',
          '  align-items: center;',
          '  gap: 4px;',
          '  padding: 12px 16px;',
          '  background: rgba(99, 102, 241, 0.03);',
          '  border-bottom: 1px solid rgba(99, 102, 241, 0.1);',
          '  flex-wrap: wrap;',
          '}',
          '.tiptap-toolbar button {',
          '  display: flex;',
          '  align-items: center;',
          '  justify-content: center;',
          '  padding: 6px 10px;',
          '  border: none;',
          '  background: transparent;',
          '  border-radius: 4px;',
          '  cursor: pointer;',
          '  font-size: 14px;',
          '  color: #64748b;',
          '  transition: all 0.2s;',
          '  min-width: 32px;',
          '  height: 32px;',
          '}',
          '.tiptap-toolbar button:hover {',
          '  background: rgba(99, 102, 241, 0.1);',
          '  color: #4338ca;',
          '}',
          '.tiptap-toolbar button.is-active {',
          '  background: rgba(99, 102, 241, 0.15);',
          '  color: #4338ca;',
          '  font-weight: 600;',
          '}',
          '.toolbar-divider {',
          '  width: 1px;',
          '  height: 24px;',
          '  background: #e5e7eb;',
          '  margin: 0 4px;',
          '}',
          '.tiptap-color-menu {',
          '  position: absolute;',
          '  top: 100%;',
          '  left: 0;',
          '  margin-top: 4px;',
          '  background: white;',
          '  border: 1px solid rgba(99, 102, 241, 0.2);',
          '  border-radius: 8px;',
          '  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);',
          '  z-index: 1000;',
          '  padding: 8px;',
          '  display: flex;',
          '  flex-direction: column;',
          '  gap: 4px;',
          '  min-width: 160px;',
          '  max-height: 300px;',
          '  overflow-y: auto;',
          '}',
          '.tiptap-color-menu button {',
          '  display: flex;',
          '  align-items: center;',
          '  padding: 8px 12px;',
          '  border: none;',
          '  background: transparent;',
          '  border-radius: 4px;',
          '  cursor: pointer;',
          '  font-size: 13px;',
          '  color: #64748b;',
          '  transition: all 0.2s;',
          '  text-align: left;',
          '  width: 100%;',
          '  height: auto;',
          '  min-height: 36px;',
          '}',
          '.tiptap-color-menu button:hover {',
          '  background: rgba(99, 102, 241, 0.1);',
          '  color: #4338ca;',
          '}',
          '.tiptap-color-menu button.is-active {',
          '  background: rgba(99, 102, 241, 0.15);',
          '  color: #4338ca;',
          '  font-weight: 600;',
          '}',
          '.therapy-tiptap-editor {',
          '  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu",',
          '    "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif;',
          '  font-size: 15px;',
          '  line-height: 1.7;',
          '  color: #1f2937;',
          '  padding: 24px 32px;',
          `  min-height: ${editorMinHeight};`,
          '  height: 100%;',
          '  flex: 1;',
          '  outline: none;',
          '  overflow-y: auto;',
          '}',
          '.therapy-tiptap-editor p {',
          '  margin: 0.6em 0;',
          '}',
          '.therapy-tiptap-editor p.is-editor-empty:first-child::before {',
          '  content: attr(data-placeholder);',
          '  float: left;',
          '  color: rgba(0, 0, 0, 0.4);',
          '  pointer-events: none;',
          '  height: 0;',
          '}',
          '.therapy-tiptap-editor h1 {',
          '  font-size: 48px;',
          '  font-weight: 700;',
          '  line-height: 1.04;',
          '  letter-spacing: -0.015em;',
          '  margin-top: 0.6em;',
          '  margin-bottom: 0.4em;',
          '}',
          '.therapy-tiptap-editor h2 {',
          '  font-size: 36px;',
          '  font-weight: 700;',
          '  line-height: 1.15;',
          '  letter-spacing: -0.015em;',
          '  margin-top: 0.6em;',
          '  margin-bottom: 0.4em;',
          '}',
          '.therapy-tiptap-editor h3 {',
          '  font-size: 28px;',
          '  font-weight: 600;',
          '  line-height: 1.22;',
          '  margin-top: 0.6em;',
          '  margin-bottom: 0.4em;',
          '}',
          '.therapy-tiptap-editor .tiptap-link {',
          '  color: #1a8917;',
          '  text-decoration: none;',
          '}',
          '.therapy-tiptap-editor .tiptap-link:hover {',
          '  text-decoration: underline;',
          '}',
          '.therapy-tiptap-editor .tiptap-blockquote {',
          '  border-left: 3px solid rgba(0, 0, 0, 0.84);',
          '  padding-left: 20px;',
          '  margin: 1.5em 0;',
          '  font-style: italic;',
          '  color: rgba(0, 0, 0, 0.68);',
          '}',
          '.therapy-tiptap-editor .tiptap-image {',
          '  max-width: 100%;',
          '  height: auto;',
          '  display: block;',
          '  margin: 2em auto;',
          '  border-radius: 4px;',
          '}',
          '.therapy-tiptap-editor ul,',
          '.therapy-tiptap-editor ol {',
          '  padding-left: 1.5em;',
          '  margin: 0.6em 0;',
          '}',
          '.therapy-tiptap-editor li {',
          '  margin: 0.3em 0;',
          '}',
          '@media (max-width: 768px) {',
          '  .therapy-free-form-editor-tiptap {',
          `    min-height: ${contentMinHeight != null ? wrapperMinHeight : '400px'};`,
          '  }',
          '  .therapy-tiptap-editor {',
          '    padding: 20px 16px;',
          '    font-size: 14px;',
          `    min-height: ${contentMinHeight != null ? editorMinHeight : '350px'};`,
          '  }',
          '  .therapy-tiptap-editor p.is-editor-empty:first-child::before {',
          '    left: 16px;',
          '    right: 16px;',
          '    font-size: 14px;',
          '  }',
          '}',
        ].join('\n')
      }} />
    </div>
  )
}

export default TipTapEditor
